/**
 * Single source of truth for languages exposed in the Meta-targeting UI.
 *
 * Why this file exists
 * --------------------
 * Meta's Marketing API targets languages by **numeric locale IDs** that are
 * NOT BCP-47 codes and are NOT documented as a static enum — they live in
 * Meta's `/search?type=adlocale` catalogue. Hardcoding `25 = Polish` (or any
 * other guess) is how you ship a campaign that targets the wrong language in
 * Ads Manager.
 *
 * The fix: this catalogue uses **stable string keys** (the `key` field
 * Meta returns in `/search?type=adlocale`, e.g. `pl_PL`). Numeric IDs are
 * resolved at the boundary against Meta's live API by
 * `server/services/meta-locale-resolver.ts`. The frontend, prompts, and DB
 * never store numeric IDs.
 */

export interface AdLocaleEntry {
  /** Meta `adlocale.key`, e.g. `pl_PL`. Stable across SDK versions. */
  readonly key: string;
  /** Human-readable label shown in the UI dropdown (flag + native name). */
  readonly label: string;
  /**
   * Canonical ENGLISH locale name sent as Meta `/search?type=adlocale` `q=`.
   * Meta matches `q` against the locale NAME (e.g. "Polish"), NOT the key —
   * sending the key (`pl_PL`) returns 0 candidates. Spelling need not be
   * byte-identical to Meta's catalogue; correctness is guaranteed by the
   * exact key-match in the resolver, this only has to surface the desired
   * key within the first 50 results.
   */
  readonly searchName: string;
  /** Aliases the AI may emit for this key (lowercase). Used to normalise LLM output. */
  readonly aliases: readonly string[];
}

export const META_AD_LOCALES: readonly AdLocaleEntry[] = [
  { key: "pl_PL", label: "🇵🇱 Polski", searchName: "Polish", aliases: ["polski", "polish", "pl"] },
  {
    key: "en_US",
    label: "🇺🇸 English (US)",
    searchName: "English (US)",
    aliases: ["english", "angielski", "en", "en-us"],
  },
  {
    key: "en_GB",
    label: "🇬🇧 English (UK)",
    searchName: "English (UK)",
    aliases: ["en-gb", "british english"],
  },
  {
    key: "de_DE",
    label: "🇩🇪 Deutsch",
    searchName: "German",
    aliases: ["niemiecki", "german", "de"],
  },
  {
    key: "fr_FR",
    label: "🇫🇷 Français",
    searchName: "French (France)",
    aliases: ["francuski", "french", "fr"],
  },
  {
    key: "es_ES",
    label: "🇪🇸 Español",
    searchName: "Spanish",
    aliases: ["hiszpański", "hiszpanski", "spanish", "es"],
  },
  {
    key: "it_IT",
    label: "🇮🇹 Italiano",
    searchName: "Italian",
    aliases: ["włoski", "wloski", "italian", "it"],
  },
  {
    key: "nl_NL",
    label: "🇳🇱 Nederlands",
    searchName: "Dutch",
    aliases: ["holenderski", "dutch", "nl"],
  },
  {
    key: "pt_PT",
    label: "🇵🇹 Português",
    searchName: "Portuguese (Portugal)",
    aliases: ["portugalski", "portuguese-pt", "pt"],
  },
  {
    key: "pt_BR",
    label: "🇧🇷 Português (BR)",
    searchName: "Portuguese (Brazil)",
    aliases: ["brazylijski", "portuguese-br", "pt-br"],
  },
  {
    key: "sv_SE",
    label: "🇸🇪 Svenska",
    searchName: "Swedish",
    aliases: ["szwedzki", "swedish", "sv"],
  },
  {
    key: "da_DK",
    label: "🇩🇰 Dansk",
    searchName: "Danish",
    aliases: ["duński", "duński", "danish", "da"],
  },
  {
    key: "nb_NO",
    label: "🇳🇴 Norsk",
    searchName: "Norwegian (bokmal)",
    aliases: ["norweski", "norwegian", "no", "nb"],
  },
  {
    key: "fi_FI",
    label: "🇫🇮 Suomi",
    searchName: "Finnish",
    aliases: ["fiński", "finski", "finnish", "fi"],
  },
  { key: "cs_CZ", label: "🇨🇿 Čeština", searchName: "Czech", aliases: ["czeski", "czech", "cs"] },
  {
    key: "sk_SK",
    label: "🇸🇰 Slovenčina",
    searchName: "Slovak",
    aliases: ["słowacki", "slowacki", "slovak", "sk"],
  },
  {
    key: "hu_HU",
    label: "🇭🇺 Magyar",
    searchName: "Hungarian",
    aliases: ["węgierski", "wegierski", "hungarian", "hu"],
  },
  {
    key: "ro_RO",
    label: "🇷🇴 Română",
    searchName: "Romanian",
    aliases: ["rumuński", "rumunski", "romanian", "ro"],
  },
  {
    key: "uk_UA",
    label: "🇺🇦 Українська",
    searchName: "Ukrainian",
    aliases: ["ukraiński", "ukrainski", "ukrainian", "uk"],
  },
  {
    key: "tr_TR",
    label: "🇹🇷 Türkçe",
    searchName: "Turkish",
    aliases: ["turecki", "turkish", "tr"],
  },
  {
    key: "ru_RU",
    label: "🇷🇺 Русский",
    searchName: "Russian",
    aliases: ["rosyjski", "russian", "ru"],
  },
  {
    key: "ja_JP",
    label: "🇯🇵 日本語",
    searchName: "Japanese",
    aliases: ["japoński", "japonski", "japanese", "ja"],
  },
  {
    key: "ko_KR",
    label: "🇰🇷 한국어",
    searchName: "Korean",
    aliases: ["koreański", "koreanski", "korean", "ko"],
  },
  { key: "ar_AR", label: "🇸🇦 العربية", searchName: "Arabic", aliases: ["arabski", "arabic", "ar"] },
  { key: "he_IL", label: "🇮🇱 עברית", searchName: "Hebrew", aliases: ["hebrajski", "hebrew", "he"] },
];

const KEY_SET = new Set<string>(META_AD_LOCALES.map((l) => l.key));
const ALIAS_TO_KEY = new Map<string, string>();
for (const entry of META_AD_LOCALES) {
  ALIAS_TO_KEY.set(entry.key.toLowerCase(), entry.key);
  for (const alias of entry.aliases) {
    ALIAS_TO_KEY.set(alias.toLowerCase(), entry.key);
  }
}

const KEY_TO_SEARCH_NAME = new Map<string, string>(
  META_AD_LOCALES.map((l) => [l.key, l.searchName])
);

/** Returns true iff the value is a known Meta locale key (case-sensitive `xx_YY`). */
export function isAdLocaleKey(value: unknown): value is string {
  return typeof value === "string" && KEY_SET.has(value);
}

/**
 * Returns the English search name for a known locale key (used as Meta's
 * `/search?type=adlocale` `q=`), or `null` if the key is unknown.
 */
export function getAdLocaleSearchName(key: string): string | null {
  return KEY_TO_SEARCH_NAME.get(key) ?? null;
}

/**
 * Maps a free-form language token (key, alias, or display label) to its
 * canonical key, or `null` if unrecognised. Use this to normalise LLM output
 * or legacy data before sending to the Meta-locale resolver.
 */
export function normaliseAdLocaleKey(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (KEY_SET.has(trimmed)) return trimmed;
  return ALIAS_TO_KEY.get(trimmed.toLowerCase()) ?? null;
}

/** Compact bullet-list of (key, label) pairs to embed in AI prompts. */
export function formatAdLocalesForPrompt(): string {
  return META_AD_LOCALES.map((l) => `- ${l.key} (${l.label.replace(/^\p{Emoji}+\s*/u, "")})`).join(
    "\n"
  );
}
